import React, { useState, useEffect } from "react";
import {
  Fab,
  Zoom,
  Tooltip,
  Box,
  Badge,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
} from "@mui/material";
import {
  WhatsApp,
  Close,
  KeyboardArrowUp,
  SupportAgent,
  ShoppingCart,
  Phone,
} from "@mui/icons-material";
import { useWhatsAppMessage } from "../../hooks/useWhatsAppMessage";
import { CONTACT_INFO } from "../../constants/contactInfo";

const FloatingWhatsAppButton = ({
  showNotificationDelay = 3000,
  hideNotificationDelay = 10000,
}) => {
  const [visible, setVisible] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Hook inteligente para mensajes por ruta
  const message = useWhatsAppMessage();

  // 🆕 ACTUALIZADO: Usar números reales desde CONTACT_INFO
  const whatsappContacts = CONTACT_INFO.phones
    .filter((phone) => phone.whatsapp) // Solo números con WhatsApp
    .map((phone, index) => ({
      id: `whatsapp-${index}`,
      name: phone.label,
      phone: phone.number.replace(/[\s+]/g, ""), // Limpiar formato
      icon: <Phone />,
      description: "Atención al cliente",
      available: true,
      color: phone.primary ? "#25D366" : "#128C7E",
      type: "individual",
    }));

  useEffect(() => {
    // Mostrar el botón después de 2 segundos
    const timer1 = setTimeout(() => {
      setVisible(true);
    }, 2000);

    // Mostrar notificación después del delay especificado
    const timer2 = setTimeout(() => {
      setShowNotification(true);
    }, showNotificationDelay);

    // Ocultar notificación después del delay especificado
    const timer3 = setTimeout(() => {
      setShowNotification(false);
    }, hideNotificationDelay);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [showNotificationDelay, hideNotificationDelay]);

  // 🆕 NUEVO: Manejar click del botón principal
  const handleMainButtonClick = (event) => {
    // Si hay múltiples contactos disponibles, mostrar menú
    const availableContacts = whatsappContacts.filter(
      (contact) => contact.available
    );

    if (availableContacts.length > 1) {
      setMenuAnchor(event.currentTarget);
      setShowNotification(false);
    } else {
      // Si solo hay uno disponible, ir directo a WhatsApp
      const contact = availableContacts[0];
      handleWhatsAppClick(contact.phone, contact.name);
    }
  };

  // 🆕 MEJORADO: Manejar click en contacto específico (individual o grupo)
  const handleContactClick = (contact) => {
    if (contact.type === "group") {
      handleGroupInvite(contact);
    } else {
      handleWhatsAppClick(contact.phone, contact.name);
    }
    setMenuAnchor(null);
  };

  // 🆕 NUEVO: Función para manejar invitaciones a grupos
  const handleGroupInvite = (contact) => {
    // URL para unirse al grupo de WhatsApp
    const groupInviteUrl = `https://chat.whatsapp.com/${contact.groupInviteCode}`;
    window.open(groupInviteUrl, "_blank");
    setShowNotification(false);
  };

  const handleWhatsAppClick = (phoneNumber, contactName = "") => {
    let customMessage = message;

    // Personalizar mensaje según el contacto
    if (contactName) {
      customMessage = `${message}`;
    }

    const encodedMessage = encodeURIComponent(customMessage);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
    setShowNotification(false);
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  // Contar contactos disponibles
  const availableContacts = whatsappContacts.filter(
    (contact) => contact.available
  );
  const isMenuOpen = Boolean(menuAnchor);

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: isMobile ? 16 : 24,
        right: isMobile ? 16 : 24,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 2,
      }}
    >
      {/* Notificación inteligente mejorada */}
      <Zoom in={showNotification && !isMenuOpen}>
        <Paper
          elevation={8}
          sx={{
            p: 2,
            display: showNotification && !isMenuOpen ? "unset" : "none",
            backgroundColor: "white",
            borderRadius: 2,
            maxWidth: isMobile ? 280 : 340,
            position: "relative",
            border: "1px solid #e0e0e0",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              right: isMobile ? 15 : 20,
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: "8px solid white",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#25D366",
                }}
              />
              <Typography
                variant="subtitle2"
                component={"span"}
                fontWeight="bold"
                color="primary"
              >
                Tattersall en línea
              </Typography>
            </Box>
            <Box
              sx={{
                cursor: "pointer",
                ml: 1,
                "&:hover": { backgroundColor: "#f5f5f5" },
                borderRadius: 1,
                p: 0.5,
              }}
              onClick={handleCloseNotification}
            >
              <Close fontSize="small" />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ¡Hola! 👋 ¿En qué podemos ayudarte hoy?
          </Typography>

          {/* Mostrar opciones de contacto en la notificación */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {availableContacts.slice(0, 2).map((contact) => (
              <Box
                key={contact.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "0.8rem",
                  color: "text.secondary",
                }}
              >
                <Box sx={{ color: contact.color }}>{contact.icon}</Box>
                <Typography variant="caption">{contact.name}</Typography>
              </Box>
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary">
            {availableContacts.length > 1
              ? "Haz clic para elegir la línea correcta"
              : "Respuesta típica en pocos minutos"}
          </Typography>
        </Paper>
      </Zoom>

      {/* Menú de contactos */}
      <Menu
        disableScrollLock
        anchorEl={menuAnchor}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              minWidth: 280,
              borderRadius: 2,
              mt: -1,
            },
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary">
            ¿A qué línea quieres escribir?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Selecciona una de nuestras líneas directas
          </Typography>
        </Box>

        <Divider />

        {whatsappContacts.map((contact) => (
          <MenuItem
            key={contact.id}
            onClick={() => handleContactClick(contact)}
            disabled={!contact.available}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: contact.available
                  ? `${contact.color}08`
                  : undefined,
              },
            }}
          >
            <ListItemIcon sx={{ color: contact.color, minWidth: 40 }}>
              {contact.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {contact.name}
                  </Typography>
                  {!contact.available && (
                    <Chip
                      label="No disponible"
                      size="small"
                      color="default"
                      sx={{ height: 18, fontSize: "0.7rem" }}
                    />
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {contact.description}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="primary"
                    fontWeight="bold"
                  >
                    {
                      CONTACT_INFO.phones.find(
                        (p) => p.number.replace(/[\s+]/g, "") === contact.phone
                      )?.number
                    }
                  </Typography>
                </Box>
              }
              secondaryTypographyProps={{
                variant: "caption",
                color: "text.secondary",
              }}
            />
            {contact.available && (
              <WhatsApp sx={{ fontSize: 20, color: "#25D366", ml: 1 }} />
            )}
          </MenuItem>
        ))}

        <Divider />

        <Box sx={{ p: 1.5, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary" display="block">
            <Phone sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }} />
            También puedes llamarnos directamente
          </Typography>
        </Box>
      </Menu>

      {/* Botón WhatsApp principal con indicador de menú */}
      <Zoom in={visible}>
        <Tooltip
          title={
            availableContacts.length > 1
              ? `¡Chatea con nosotros! (${availableContacts.length} líneas disponibles)`
              : "¡Chatea con nosotros!"
          }
          placement="left"
          arrow
        >
          <Badge
            badgeContent={
              availableContacts.length > 1
                ? availableContacts.length
                : showNotification
                ? "1"
                : 0
            }
            color="error"
            invisible={!showNotification && availableContacts.length <= 1}
            sx={{
              "& .MuiBadge-badge": {
                zIndex: 2000,
                display: !showNotification && "none",
                animation: showNotification ? "bounce 1s infinite" : "none",
                "@keyframes bounce": {
                  "0%, 20%, 50%, 80%, 100%": {
                    transform: "translateY(0)",
                  },
                  "40%": {
                    transform: "translateY(-10px)",
                  },
                  "60%": {
                    transform: "translateY(-5px)",
                  },
                },
              },
            }}
          >
            <Fab
              color="success"
              size={isMobile ? "medium" : "large"}
              onClick={handleMainButtonClick}
              sx={{
                backgroundColor: "#25D366",
                color: "white",
                width: isMobile ? 56 : 64,
                height: isMobile ? 56 : 64,
                "&:hover": {
                  backgroundColor: "#128C7E",
                  transform: "scale(1.1)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
                transition: "all 0.2s ease-in-out",
                boxShadow: "0 4px 20px rgba(37, 211, 102, 0.4)",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": {
                    boxShadow: "0 4px 20px rgba(37, 211, 102, 0.4)",
                  },
                  "50%": {
                    boxShadow: "0 4px 30px rgba(37, 211, 102, 0.8)",
                  },
                  "100%": {
                    boxShadow: "0 4px 20px rgba(37, 211, 102, 0.4)",
                  },
                },
                // Indicador visual de que hay menú disponible
                ...(availableContacts.length > 1 && {
                  position: "relative",
                  "&::after": {
                    display: showNotification && "none",
                    content: '""',
                    position: "absolute",
                    top: -2,
                    right: -2,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#d32f2f",
                    border: "2px solid white",
                  },
                }),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <WhatsApp sx={{ fontSize: isMobile ? 20 : 28 }} />
                {availableContacts.length > 1 && (
                  <KeyboardArrowUp
                    sx={{
                      fontSize: 12,
                      mt: -0.5,
                      animation: "fadeInOut 2s infinite",
                      "@keyframes fadeInOut": {
                        "0%, 100%": { opacity: 0.6 },
                        "50%": { opacity: 1 },
                      },
                    }}
                  />
                )}
              </Box>
            </Fab>
          </Badge>
        </Tooltip>
      </Zoom>
    </Box>
  );
};

export default FloatingWhatsAppButton;
